import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Stack, Chip,
  IconButton, TextField, MenuItem, Tooltip, LinearProgress,
  Divider, Paper
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon,
  AssignmentTurnedIn as TaskIcon,
  ArrowBack as ArrowBackIcon, CheckCircle as CheckCircleIcon, Schedule as ScheduleIcon,
  Person as PersonIcon, Flag as FlagIcon, Folder as ProjectIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { projectApi } from '../services/projectApi';
import { PrjTask, TaskStatus, TaskPriority, PrjProject } from '../types/project';

const emptyTask = (): Partial<PrjTask> => ({
  projectId: undefined,
  title: '',
  status: 'TODO',
  priority: 'MEDIUM',
  assignee: '',
  dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  estimatedHours: 8,
  actualHours: 0,
  progress: 0,
  description: '',
});

const getStatusColor = (status: TaskStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'TODO': return 'default';
    case 'IN_PROGRESS': return 'primary';
    case 'REVIEW': return 'warning';
    case 'DONE': return 'success';
    case 'BLOCKED': return 'error';
    default: return 'default';
  }
};

const getStatusLabel = (status: TaskStatus, t: (key: string, fallback?: string) => string): string => {
  switch (status) {
    case 'TODO': return t('project.tasks.statusTodo', 'À faire');
    case 'IN_PROGRESS': return t('project.tasks.statusInProgress', 'En cours');
    case 'REVIEW': return t('project.tasks.statusReview', 'En révision');
    case 'DONE': return t('project.tasks.statusDone', 'Terminé');
    case 'BLOCKED': return t('project.tasks.statusBlocked', 'Bloqué');
    default: return status;
  }
};

const getPriorityColor = (priority: TaskPriority): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (priority) {
    case 'CRITICAL': return 'error';
    case 'HIGH': return 'warning';
    case 'MEDIUM': return 'info';
    case 'LOW': return 'default';
    default: return 'default';
  }
};

export const TasksPage: React.FC = () => {
  const { t } = useLanguage();

  const [view, setView] = useState<'list' | 'form'>('list');
  const [viewMode, setViewMode] = useDefaultViewMode();
  const [tasksList, setTasksList] = useState<PrjTask[]>([]);
  const [projectsList, setProjectsList] = useState<PrjProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<PrjTask>>(emptyTask());
  const [isEditing, setIsEditing] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tasks, projects] = await Promise.all([
        projectApi.getTasks(),
        projectApi.getProjects()
      ]);
      setTasksList(tasks);
      setProjectsList(projects);
    } catch (err) {
      console.error('Error loading tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openNew = () => {
    setFormData(emptyTask());
    setIsEditing(false);
    setView('form');
  };

  const openEdit = (task: PrjTask) => {
    setFormData({ ...task });
    setIsEditing(true);
    setView('form');
  };

  const backToList = () => {
    setView('list');
  };

  const handleDelete = async (task: PrjTask) => {
    const taskId = (task.id || task.PTK_IDF)!;
    const confirmed = await blobticMessage.confirm({
      message: t('project.tasks.confirmDelete', 'Êtes-vous sûr de vouloir supprimer cette tâche ?'),
      title: t('common.confirm', 'Confirmation')
    });

    if (confirmed) {
      const res = await projectApi.deleteTask(taskId);
      if (res.success) {
        blobticMessage.success(t('project.tasks.deleteSuccess', 'Tâche supprimée avec succès.'));
        loadData();
      } else {
        blobticMessage.error(res.message || t('common.error', 'Erreur lors de la suppression.'));
      }
    }
  };

  const handleToggleDone = async (task: PrjTask) => {
    const taskId = (task.id || task.PTK_IDF)!;
    const nextStatus: TaskStatus = task.status === 'DONE' ? 'IN_PROGRESS' : 'DONE';
    const nextProgress = nextStatus === 'DONE' ? 100 : task.progress;
    const res = await projectApi.updateTask({
      PTK_IDF: taskId,
      PTK_STATUS: nextStatus,
      PTK_PROGRESS: nextProgress,
    });
    if (res.success) {
      loadData();
    }
  };

  const handleValidate = (): string | null => {
    if (!formData.title?.trim()) {
      return t('project.tasks.titleRequired', 'Le titre de la tâche est requis.');
    }
    if (!formData.projectId && !formData.PRJ_IDF) {
      return t('project.tasks.projectRequired', 'Veuillez sélectionner un projet associé.');
    }
    return null;
  };

  const handleDataChange = () => {
    backToList();
    loadData();
  };

  if (view === 'form') {
    const formPayload = {
      ...formData,
      PTK_IDF: formData.id || formData.PTK_IDF,
      PTK_CODE: formData.code || formData.PTK_CODE,
      PRJ_IDF: formData.projectId || formData.PRJ_IDF,
      PTK_TITLE: formData.title,
      PTK_STATUS: formData.status,
      PTK_PRIORITY: formData.priority,
      PTK_ASSIGNEE: formData.assignee,
      PTK_DUE_DATE: formData.dueDate,
      PTK_ESTIMATED_HOURS: Number(formData.estimatedHours || 0),
      PTK_ACTUAL_HOURS: Number(formData.actualHours || 0),
      PTK_PROGRESS: Number(formData.progress || 0),
      PTK_DESCRIPTION: formData.description,
    };

    return (
      <GenericPage<any>
        title={isEditing ? t('project.tasks.editTitle', 'Modifier la Tâche') : t('project.tasks.newTitle', 'Nouvelle Tâche')}
        icon={<TaskIcon />}
        footerEnabled={true}
        footerInfo={formData.last_updt ? { lastUpdate: formData.last_updt, userLib: formData.user_lib } : undefined}
        additionalActions={
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={backToList}
          >
            {t('common.backToList', 'Retour à la liste')}
          </Button>
        }
        crudConfig={{ entityKey: 'Blobtic_ProjectManagement,prj_task', primaryKey: 'PTK_IDF' }}
        data={formPayload}
        onDataChange={handleDataChange}
        onValidate={handleValidate}
        onClear={() => setFormData(emptyTask())}
      >
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {t('project.tasks.details', 'Détails de la tâche')}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label={t('project.tasks.project', 'Projet associé *')}
                value={formData.projectId || formData.PRJ_IDF || ''}
                onChange={(e) => setFormData({ ...formData, projectId: Number(e.target.value), PRJ_IDF: Number(e.target.value) })}
                required
              >
                {projectsList.map((p) => (
                  <MenuItem key={p.id || p.PRJ_IDF} value={p.id || p.PRJ_IDF}>
                    {p.title} ({p.code || `PRJ-${p.id}`})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('project.tasks.title', 'Titre de la tâche *')}
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label={t('project.tasks.status', 'Statut')}
                value={formData.status || 'TODO'}
                onChange={(e) => {
                  const status = e.target.value as TaskStatus;
                  const progress = status === 'DONE' ? 100 : (formData.progress || 0);
                  setFormData({ ...formData, status, progress });
                }}
              >
                <MenuItem value="TODO">{t('project.tasks.statusTodo', 'À faire')}</MenuItem>
                <MenuItem value="IN_PROGRESS">{t('project.tasks.statusInProgress', 'En cours')}</MenuItem>
                <MenuItem value="REVIEW">{t('project.tasks.statusReview', 'En révision')}</MenuItem>
                <MenuItem value="DONE">{t('project.tasks.statusDone', 'Terminé')}</MenuItem>
                <MenuItem value="BLOCKED">{t('project.tasks.statusBlocked', 'Bloqué')}</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label={t('project.tasks.priority', 'Priorité')}
                value={formData.priority || 'MEDIUM'}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as TaskPriority })}
              >
                <MenuItem value="LOW">{t('project.tasks.priorityLow', 'Basse')}</MenuItem>
                <MenuItem value="MEDIUM">{t('project.tasks.priorityMedium', 'Moyenne')}</MenuItem>
                <MenuItem value="HIGH">{t('project.tasks.priorityHigh', 'Haute')}</MenuItem>
                <MenuItem value="CRITICAL">{t('project.tasks.priorityCritical', 'Critique')}</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t('project.tasks.assignee', 'Assigné à')}
                value={formData.assignee || ''}
                onChange={(e) => setFormData({ ...formData, assignee: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                type="date"
                label={t('project.tasks.dueDate', 'Échéance')}
                value={formData.dueDate || ''}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                type="number"
                label={t('project.tasks.estimatedHours', 'Heures estimées')}
                value={formData.estimatedHours ?? 0}
                onChange={(e) => setFormData({ ...formData, estimatedHours: parseFloat(e.target.value) || 0 })}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                type="number"
                label={t('project.tasks.actualHours', 'Heures passées')}
                value={formData.actualHours ?? 0}
                onChange={(e) => setFormData({ ...formData, actualHours: parseFloat(e.target.value) || 0 })}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                type="number"
                label={t('project.tasks.progress', 'Progression (%)')}
                value={formData.progress ?? 0}
                onChange={(e) => setFormData({ ...formData, progress: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) })}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('project.tasks.description', 'Description détaillée')}
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </Paper>
      </GenericPage>
    );
  }

  // Columns for GenericTable
  const columns = [
    {
      id: 'code',
      label: t('project.tasks.code', 'Code'),
      minWidth: 90,
      format: (_: any, row: PrjTask) => (
        <Typography variant="body2" fontWeight={600} color="primary">
          {row.code || `TSK-${row.id}`}
        </Typography>
      )
    },
    {
      id: 'title',
      label: t('project.tasks.title', 'Tâche & Projet'),
      minWidth: 220,
      format: (_: any, row: PrjTask) => (
        <Box>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ textDecoration: row.status === 'DONE' ? 'line-through' : 'none' }}
          >
            {row.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.projectTitle || `Projet #${row.projectId}`}
          </Typography>
        </Box>
      )
    },
    {
      id: 'assignee',
      label: t('project.tasks.assignee', 'Assigné'),
      minWidth: 130,
      format: (_: any, row: PrjTask) => (
        <Typography variant="body2">
          {row.assignee || '-'}
        </Typography>
      )
    },
    {
      id: 'priority',
      label: t('project.tasks.priority', 'Priorité'),
      minWidth: 100,
      format: (_: any, row: PrjTask) => (
        <Chip
          label={row.priority}
          color={getPriorityColor(row.priority)}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      id: 'dueDate',
      label: t('project.tasks.dueDate', 'Échéance'),
      minWidth: 110,
      format: (_: any, row: PrjTask) => (
        <Typography variant="body2" color="text.secondary">
          {row.dueDate || '-'}
        </Typography>
      )
    },
    {
      id: 'progress',
      label: t('project.tasks.progress', 'Progression'),
      minWidth: 120,
      format: (_: any, row: PrjTask) => (
        <Box sx={{ width: '100%' }}>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" fontWeight={600}>{row.progress || 0}%</Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={row.progress || 0}
            color={row.progress >= 100 ? 'success' : 'primary'}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      )
    },
    {
      id: 'status',
      label: t('project.tasks.status', 'Statut'),
      minWidth: 120,
      format: (_: any, row: PrjTask) => (
        <Chip
          label={getStatusLabel(row.status, t)}
          color={getStatusColor(row.status)}
          size="small"
          variant={row.status === 'DONE' ? 'filled' : 'outlined'}
          sx={{ fontWeight: 600 }}
        />
      )
    }
  ];

  return (
    <GenericPage
      title={t('project.tasks.pageTitle', 'Gestion des Tâches')}
      icon={<TaskIcon />}
      footerEnabled={false}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      onNew={openNew}
      newButtonLabel={t('project.tasks.new', 'Nouvelle Tâche')}
    >
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {viewMode === 'grid' ? (
        <Grid container spacing={2.5}>
          {tasksList.map((task) => (
            <Grid item xs={12} sm={6} md={4} key={task.id || task.PTK_IDF}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                  borderLeft: task.status === 'DONE' ? '4px solid #4caf50' : '4px solid #1976d2',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 6px 18px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {task.code || `TSK-${task.id}`}
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{
                          lineHeight: 1.25,
                          mt: 0.25,
                          textDecoration: task.status === 'DONE' ? 'line-through' : 'none',
                          color: task.status === 'DONE' ? 'text.secondary' : 'text.primary'
                        }}
                      >
                        {task.title}
                      </Typography>
                    </Box>
                    <Chip
                      label={getStatusLabel(task.status, t)}
                      color={getStatusColor(task.status)}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Stack>

                  {task.projectTitle && (
                    <Stack direction="row" spacing={0.8} alignItems="center" mb={1.5}>
                      <ProjectIcon fontSize="small" sx={{ color: 'action.active' }} />
                      <Typography variant="caption" color="primary" fontWeight={600}>
                        {task.projectTitle}
                      </Typography>
                    </Stack>
                  )}

                  <Box sx={{ my: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                      <Typography variant="caption" color="text.secondary">{t('project.tasks.progress', 'Progression')}</Typography>
                      <Typography variant="caption" fontWeight={700}>{task.progress || 0}%</Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={task.progress || 0}
                      color={task.progress >= 100 ? 'success' : 'primary'}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>

                  <Stack spacing={0.8} sx={{ color: 'text.secondary', fontSize: '0.85rem', mb: 2 }}>
                    {task.assignee && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PersonIcon fontSize="small" sx={{ color: 'action.active' }} />
                        <Typography variant="body2">{task.assignee}</Typography>
                      </Stack>
                    )}
                    {task.dueDate && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ScheduleIcon fontSize="small" sx={{ color: 'action.active' }} />
                        <Typography variant="body2">{t('project.tasks.due', 'Échéance')}: {task.dueDate}</Typography>
                      </Stack>
                    )}
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TimeIcon fontSize="small" sx={{ color: 'action.active' }} />
                      <Typography variant="caption">
                        {task.actualHours || 0}h / {task.estimatedHours || 0}h {t('project.tasks.hours', 'estimées')}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Divider sx={{ my: 1.5 }} />

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Chip
                      icon={<FlagIcon fontSize="small" />}
                      label={task.priority}
                      color={getPriorityColor(task.priority)}
                      size="small"
                      variant="outlined"
                    />

                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title={task.status === 'DONE' ? t('project.tasks.markIncomplete', 'Marquer non terminée') : t('project.tasks.markDone', 'Marquer terminée')}>
                        <IconButton size="small" color={task.status === 'DONE' ? 'success' : 'default'} onClick={() => handleToggleDone(task)}>
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.edit', 'Modifier')}>
                        <IconButton size="small" color="primary" onClick={() => openEdit(task)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete', 'Supprimer')}>
                        <IconButton size="small" color="error" onClick={() => handleDelete(task)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}

          {tasksList.length === 0 && !loading && (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                <Typography color="text.secondary">
                  {t('project.tasks.empty', 'Aucune tâche trouvée. Cliquez sur "Nouvelle Tâche" pour commencer.')}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      ) : (
        <GenericTable
          columns={columns}
          data={tasksList}
          filterColumns={['title', 'projectTitle', 'assignee', 'code', 'status']}
          searchPlaceholder={t('project.tasks.searchPlaceholder', 'Rechercher une tâche par titre, projet, assigné...')}
          maxHeight="calc(100vh - 280px)"
          onEdit={openEdit}
          onDelete={handleDelete}
          rowActions={(row: PrjTask) => (
            <Tooltip title={row.status === 'DONE' ? t('project.tasks.markIncomplete', 'Marquer non terminée') : t('project.tasks.markDone', 'Marquer terminée')}>
              <IconButton size="small" color={row.status === 'DONE' ? 'success' : 'default'} onClick={() => handleToggleDone(row)}>
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        />
      )}
    </GenericPage>
  );
};

export default TasksPage;
